import { useState, useEffect } from "react";
import { showToast } from "../toast";
import { config } from "../../config/environment";
import { API_ENDPOINTS } from "../../config/api-endpoints";

console.log("config.api.baseUrl", config.api.baseUrl);
console.log("API_ENDPOINTS", API_ENDPOINTS);
console.log("config", config);

const baseURL = config.api.baseUrl + "/" + config.api.version;
console.log("baseURL", baseURL);
let unauthorizedHandled = false;

// Enhanced error types
interface ApiError {
  message: string;
  code?: string;
  details?: any;
  statusCode: number;
}

// Request configuration interface
interface RequestConfig {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
}

export default function useRequest(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  headers?: Record<string, any>,
  requestConfig?: RequestConfig
) {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>();
  const [statusCode, setStatusCode] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);

  const token = localStorage.getItem(config.auth.tokenKey);
  const timeout = requestConfig?.timeout || config.api.timeout;

  useEffect(() => {
    return () => {
      unauthorizedHandled = false;
    };
  }, []);

  // Helper function to handle unauthorized responses
  const handleUnauthorized = () => {
    if (!unauthorizedHandled) {
      unauthorizedHandled = true;
      localStorage.removeItem(config.auth.tokenKey);
      localStorage.removeItem(config.auth.refreshTokenKey);
      showToast("Session expired. Please log in again.", false, {
        position: "top-right",
      });
      // Redirect to login page
      window.location.href = "/signin";
    }
  };

  // Enhanced error handling
  const handleError = (response: Response, json: any): ApiError => {
    const apiError: ApiError = {
      message: json.message || "An error occurred",
      code: json.code,
      details: json.details,
      statusCode: response.status,
    };

    // Handle specific status codes
    switch (response.status) {
      case 401:
        handleUnauthorized();
        apiError.message = "Unauthorized access. Please log in again.";
        break;
      case 403:
        apiError.message = "You don't have permission to perform this action.";
        break;
      case 404:
        apiError.message = "The requested resource was not found.";
        break;
      case 422:
        apiError.message = "Invalid data provided. Please check your input.";
        break;
      case 429:
        apiError.message = "Too many requests. Please try again later.";
        break;
      case 500:
        apiError.message = "Server error. Please try again later.";
        break;
      case 503:
        apiError.message = "Service temporarily unavailable.";
        break;
    }

    return apiError;
  };

  // Main request function with retry logic
  async function makeRequest(
    data?: any,
    params?: Record<string, any>,
    pathParam?: string,
    retryCount: number = 0
  ): Promise<[any, number]> {
    setLoading(true);
    setError(null);

    const queryParams = new URLSearchParams(params).toString();
    const urlWithParams =
      pathParam && queryParams
        ? `${baseURL}${endpoint}/${pathParam}?${queryParams}`
        : pathParam
        ? `${baseURL}${endpoint}/${pathParam}`
        : queryParams
        ? `${baseURL}${endpoint}?${queryParams}`
        : `${baseURL}${endpoint}`;

    console.log("Making request to:", urlWithParams);

    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      console.log(baseURL);
      console.log(urlWithParams);

      const response = await fetch(urlWithParams, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
          ...headers,
        },
        body:
          method === "POST" ||
          method === "PUT" ||
          method === "DELETE" ||
          method === "PATCH"
            ? JSON.stringify(data)
            : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let json: any = {};
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        json = await response.json();
      }

      setResponse(json);
      setStatusCode(response.status);

      // Handle error responses
      if (!response.ok) {
        const apiError = handleError(response, json);
        setError(apiError);

        // Show error toast if configured
        if (requestConfig?.showErrorToast !== false) {
          showToast(apiError.message, false, {
            position: "top-right",
          });
        }

        setLoading(false);
        return [json, response.status];
      }

      // Show success toast if configured
      if (requestConfig?.showSuccessToast && method !== "GET") {
        showToast(json.message || "Operation completed successfully", true, {
          position: "top-right",
        });
      }

      setLoading(false);
      return [json, response.status];
    } catch (error: any) {
      // Handle timeout and network errors
      if (error.name === "AbortError") {
        const timeoutError: ApiError = {
          message: "Request timed out. Please try again.",
          statusCode: 408,
        };
        setError(timeoutError);
      } else {
        const networkError: ApiError = {
          message:
            error?.message === "Failed to fetch"
              ? "Network error. Please check your connection."
              : "An unexpected error occurred.",
          statusCode: 0,
        };
        setError(networkError);

        // Retry logic for network errors
        const maxRetries = requestConfig?.retries || 0;
        if (retryCount < maxRetries && error?.message === "Failed to fetch") {
          const retryDelay = requestConfig?.retryDelay || 1000;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return makeRequest(data, params, pathParam, retryCount + 1);
        }
      }

      // Show error toast for non-GET methods or if configured
      if (
        requestConfig?.showErrorToast !== false &&
        (method !== "GET" || requestConfig?.showErrorToast === true)
      ) {
        showToast(error?.message || "An error occurred", false, {
          position: "top-right",
        });
      }

      setLoading(false);
      return [{}, error.name === "AbortError" ? 408 : 500];
    }
  }

  return {
    loading,
    makeRequest,
    response,
    statusCode,
    error,
    isSuccess: statusCode >= 200 && statusCode < 300,
    isError: statusCode >= 400 || error !== null,
  };
}
